export function buildShellMenuState(except = null, currentState = {}) {
    const nextState = {
        siteMenu: false,
        datasetSelectMenu: false,
        datasetMenu: false,
        dateMenu: false,
        viewMenu: false,
    };

    if (except && Object.prototype.hasOwnProperty.call(nextState, except)) {
        nextState[except] = Boolean(currentState[except]);
    }

    return nextState;
}

export function buildSiteSelectionResetState() {
    return {
        dataset_groups_select: null,
        datasetDisplayValue: null,
        datasets_list: [],
        datasets: [],
        datetime_select: null,
        views_select: null,
        datasetSelectMenu: false,
        datasetMenu: false,
        dateMenu: false,
        viewMenu: false,
    };
}

export function buildDatasetGroupSelectionState({
    datasetGroup,
    isAllLocationsSelected = false,
    enrichDatasets = (datasets) => datasets,
}) {
    const datasetsMap = datasetGroup?.datasets || null;
    const enrichedDatasets = datasetsMap
        ? enrichDatasets(Object.values(datasetsMap))
        : [];

    return {
        dataset_groups_select: datasetGroup?.uuid || null,
        datasetDisplayValue: isAllLocationsSelected
            ? datasetGroup?.name || null
            : null,
        datasets_list: datasetsMap,
        datasets: enrichedDatasets,
        datetime_select: null,
        views_select: null,
        datasetSelectMenu: false,
        datasetMenu: false,
        dateMenu: false,
        viewMenu: false,
    };
}

const DATASET_PACKAGE_VIEW_FIELDS = {
    inspection_view: 'inspection_view',
    cesium_tiles_view: 'cesium_tiles_view',
    ortho_view: 'ortho_view',
    thermal_inspection_view: 'thermal_inspection_view',
    bim_view: 'bim_view',
    dtm_view: 'dtm_view',
    dsm_view: 'dsm_view',
    point_cloud_view: 'point_cloud_view',
    gaussian_splat_view: 'gaussian_splat_view',
};

export function resolveShellViewType(viewType) {
    return Object.prototype.hasOwnProperty.call(DATASET_PACKAGE_VIEW_FIELDS, viewType)
        ? viewType
        : null;
}

function normaliseDatasetMap(datasets) {
    if (!datasets) {
        return {};
    }

    if (!Array.isArray(datasets)) {
        return datasets;
    }

    return datasets.reduce((map, dataset) => {
        if (dataset?.uuid) {
            map[dataset.uuid] = dataset;
        }
        return map;
    }, {});
}

export function buildDatasetFromPackage(datasetPackage) {
    if (!datasetPackage?.uuid) {
        return null;
    }

    const dataset = {
        uuid: datasetPackage.uuid,
        date: datasetPackage.date || null,
        time: datasetPackage.time || null,
        thermal: datasetPackage.thermal,
        deletion_status: datasetPackage.deletion_status || 'active',
        marked_for_deletion_at: datasetPackage.marked_for_deletion_at || null,
        can_open_viewer:
            datasetPackage.can_open_viewer ??
            datasetPackage.deletion_status === 'active',
        can_view:
            datasetPackage.can_view ??
            datasetPackage.can_open_viewer ??
            datasetPackage.deletion_status === 'active',
        can_edit: datasetPackage.can_edit ?? false,
        can_publish:
            datasetPackage.can_publish ??
            datasetPackage.deletion_status === 'active',
        can_share:
            datasetPackage.can_share ??
            datasetPackage.deletion_status === 'active',
        can_delete:
            datasetPackage.can_delete ??
            datasetPackage.deletion_status === 'active',
        deletion_badge_label: datasetPackage.deletion_badge_label || null,
    };
    const packageViews = datasetPackage.views || {};

    Object.entries(DATASET_PACKAGE_VIEW_FIELDS).forEach(
        ([datasetField, packageField]) => {
            const view = packageViews[packageField];
            dataset[datasetField] = view?.uuid || null;
            dataset[`${datasetField}_published`] = view
                ? view.state === 'published'
                : null;
        }
    );

    return dataset;
}

export function buildDatasetGroupFromPackage(
    datasetPackage,
    existingGroup = {}
) {
    if (!datasetPackage?.dataset_group_uuid) {
        return null;
    }

    const dataset = buildDatasetFromPackage(datasetPackage);
    const existingDatasets = normaliseDatasetMap(existingGroup?.datasets);
    const datasets =
        dataset?.uuid && Object.prototype.hasOwnProperty.call(dataset, 'uuid')
            ? {
                  ...existingDatasets,
                  [dataset.uuid]: {
                      ...(existingDatasets?.[dataset.uuid] || {}),
                      ...dataset,
                  },
              }
            : existingDatasets;

    return {
        ...existingGroup,
        uuid: datasetPackage.dataset_group_uuid,
        name: existingGroup?.name || datasetPackage.name || 'Selected Asset',
        site_uuid: existingGroup?.site_uuid || datasetPackage.site_uuid || null,
        site_name: existingGroup?.site_name || datasetPackage.site_name || null,
        description:
            existingGroup?.description || datasetPackage.description || null,
        datasets,
    };
}

export function hasAvailableShellDates(datasetDatetimes) {
    return Array.isArray(datasetDatetimes) && datasetDatetimes.length > 0;
}

export function hasSelectableShellViews(views) {
    return Array.isArray(views) && views.length > 0;
}

export function shouldNavigateImmediatelyForShellViewSelection({
    currentViewUuid,
    nextViewUuid,
    goToNewView = true,
}) {
    if (!nextViewUuid) {
        return false;
    }

    return currentViewUuid === nextViewUuid || !goToNewView;
}

export function shouldAutoSelectSingleDatasetGroup(datasetGroups) {
    return Array.isArray(datasetGroups) && datasetGroups.length === 1;
}

export function buildViewerMenuItems(viewerList = [], viewOptions = {}) {
    if (!Array.isArray(viewerList) || viewerList.length === 0) {
        return [];
    }

    return viewerList.filter((viewer) =>
        resolveShellViewType(viewer?.type || viewer?.viewType)
    ).map((viewer) => {
        const viewType = viewer?.type || viewer?.viewType || null;
        const option = viewType ? viewOptions[viewType] || {} : {};
        const terrainLabels = { dtm_view: 'Terrain - DTM', dsm_view: 'Terrain - DSM' };
        return {
            ...viewer,
            uuid: viewer?.uuid || viewer?.view_uuid || null,
            text:
                terrainLabels[viewType] ||
                viewer?.alias ||
                viewer?.text ||
                viewer?.label ||
                option.text ||
                'Select View',
            color: viewer?.color || option.color || null,
            icon: viewer?.icon || option.icon || null,
            notPublished: Boolean(viewer?.notPublished),
        };
    });
}

function parseShellDateTimeKey(dataset) {
    if (!dataset?.date) {
        return null;
    }

    return `${dataset.date}::${dataset.time || ''}`;
}

export function buildDatasetDatetimeOptions(datasetsList) {
    if (!Array.isArray(datasetsList) || datasetsList.length === 0) {
        return [];
    }

    const months = {
        '01': 'Jan',
        '02': 'February',
        '03': 'Mar',
        '04': 'Apr',
        '05': 'May',
        '06': 'Jun',
        '07': 'Jul',
        '08': 'Aug',
        '09': 'Sep',
        10: 'Oct',
        11: 'Nov',
        12: 'Dec',
    };
    const validDatasets = datasetsList.filter(
        (dataset) => dataset && dataset.date
    );
    const uniqueDateCounts = validDatasets.reduce((counts, dataset) => {
        counts[dataset.date] = (counts[dataset.date] || 0) + 1;
        return counts;
    }, {});

    return validDatasets
        .map((dataset) => {
            const dateParts = dataset.date.split('-');
            const display =
                uniqueDateCounts[dataset.date] > 1
                    ? `${dataset.time} ${dateParts[2]} ${months[dateParts[1]]} ${dateParts[0]}`
                    : `${dateParts[2]} ${months[dateParts[1]]} ${dateParts[0]}`;

            return {
                date: dataset.date,
                time: dataset.time || null,
                display,
            };
        })
        .sort((first, second) => {
            const firstDate = new Date(
                `${first.date || ''} ${first.time || ''}`.trim()
            );
            const secondDate = new Date(
                `${second.date || ''} ${second.time || ''}`.trim()
            );
            return secondDate - firstDate;
        });
}

export function resolveDatasetDatetimeOption(
    datasetsList,
    preferredSelection = null
) {
    const options = buildDatasetDatetimeOptions(datasetsList);
    if (!options.length) {
        return null;
    }

    const preferredKey = parseShellDateTimeKey(preferredSelection);
    if (preferredKey) {
        const preferredMatch = options.find(
            (option) => parseShellDateTimeKey(option) === preferredKey
        );
        if (preferredMatch) {
            return preferredMatch;
        }
    }

    const preferredDate = preferredSelection?.date || null;
    if (preferredDate) {
        const dateMatch = options.find((option) => option.date === preferredDate);
        if (dateMatch) {
            return dateMatch;
        }
    }

    return options[0];
}
